import { createResettableSlice } from 'careand-redux/utils/makeResettable';
import _ from 'lodash';
import { unwrap } from 'careand-redux/utils/unwrap';
import { v4 as uuidV4 } from 'uuid';
import type {
  AppointmentTranscription,
  DocumentTemplatePrefilled,
  PhysicianContact,
  PrescriptionMedication,
} from '../../generated/api';
import moment from 'moment-timezone';

export type ActionType =
  | 'prescription'
  | 'referral'
  | 'requisition'
  | 'other_document'
  | 'additional-consideration'
  | 'other';
export type ActionStatus =
  | 'ready'
  | 'in-progress'
  | 'pending-approval'
  | 'done'
  | 'rejected'
  | 'error';

type TemplatePayload = {
  id: string;
  description?: string;
  fields?: { id: string; value: string }[];
};

export type ContactPayload = {
  contact: PhysicianContact;
  reason_for_referral?: string;
  further_discussion?: string;
};

export type ActionResult = {
  prescription: { medication: PrescriptionMedication | undefined };
  referral: { templates: TemplatePayload[]; contacts: ContactPayload[] };
  requisition: { templates: TemplatePayload[]; prefilledTemplates: DocumentTemplatePrefilled[] };
  other_document: { templates: TemplatePayload[] };
  'additional-consideration': {};
  other: { note: string };
};

export function matchClinicalAction<T extends ActionType>(
  action: ClinicalAction<any>,
  ...types: T[]
): action is ClinicalAction<T> {
  return types.includes(action.type);
}

export interface ClinicalAction<TType extends ActionType> {
  id: string;
  appointment_id: string;
  description: string;
  status: ActionStatus;
  reason?: string; // Why AI suggested this
  created_at: number;
  updated_at: number;
  error_message?: string;
  type: TType;
  execution_data?: ActionResult[TType];
  executor_message?: string; // Progress message during execution
  query?: string;
  referral_name?: string;
  specialty?: string;
}

export interface AdditionalConsiderationAction extends ClinicalAction<'additional-consideration'> {
  consideration: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  rationale: string;
}

export type ExecutorState = {
  note?: string;
  transcription?: AppointmentTranscription;
  lastRunTimestamp?: number;
};

export interface ActionsState {
  // Main store
  byId: Record<string, ClinicalAction<any>>;
  executorStateByAppointmentId: Record<string, ExecutorState>;
}

const initialState: ActionsState = {
  byId: {},
  executorStateByAppointmentId: {},
};

const slice = createResettableSlice({
  name: 'Actions',
  initialState,
  reducers: {
    clearAll: unwrap((state, action: void) => {
      Object.assign(state, initialState);
    }),
    addAction: unwrap((state, action: ClinicalAction<any>) => {
      state.byId[action.id] = action;
    }),
    updateAction: unwrap((state, payload: Partial<ClinicalAction<any>> & { id: string }) => {
      const action = state.byId[payload.id];
      if (!action) {
        return;
      }
      state.byId[payload.id] = _.merge(action, payload);
    }),
    updateExecutorState: unwrap(
      (state, payload: { appointmentId: string; state: Partial<ExecutorState> }) => {
        state.executorStateByAppointmentId[payload.appointmentId] = _.merge(
          state.executorStateByAppointmentId[payload.appointmentId],
          payload.state,
        );
      },
    ),
    updateContactFields: unwrap(
      (
        state,
        payload: {
          action_id: string;
          contact_id: string;
          reason_for_referral: string;
          further_discussion: string;
        },
      ) => {
        const action = state.byId[payload.action_id] as ClinicalAction<any>;
        if (matchClinicalAction(action, 'referral')) {
          const contact = action.execution_data?.contacts.find(
            (it) => it.contact.id === payload.contact_id,
          );
          if (!contact) {
            return;
          }
          contact.further_discussion = payload.further_discussion;
          contact.reason_for_referral = payload.reason_for_referral;
        }
      },
    ),
    updateTemplateFields: unwrap(
      (
        state,
        payload: {
          action_id: string;
          template_id: string;
          fields: { id: string; value: string }[];
        },
      ) => {
        const action = state.byId[payload.action_id];
        if (!action || !matchClinicalAction(action, 'referral', 'requisition', 'other_document')) {
          return;
        }

        const template = action?.execution_data?.templates.find(
          (it) => it.id === payload.template_id,
        );
        if (!template) {
          return;
        }
        template.fields = payload.fields;
      },
    ),
    removeAction: unwrap((state, action_id: string) => {
      if (!state.byId[action_id]) {
        return;
      }
      delete state.byId[action_id];
    }),
    applyDeltas: unwrap(applyDeltas),
  },
  extraReducers: (builder) => {},
});

function applyDeltas(state: ActionsState, payload: { appointmentId: string; deltas: any[] }) {
  const { appointmentId, deltas } = payload;

  for (const delta of deltas) {
    switch (delta.change) {
      case 'add': {
        const newAction: ClinicalAction<any> = {
          id: uuidV4(),
          appointment_id: appointmentId,
          status: delta.action.type === 'additional-consideration' ? 'pending-approval' : 'ready',
          reason: delta.reason,
          ...delta.action,
          created_at: moment().unix(),
          updated_at: moment().unix(),
        };
        state.byId[newAction.id] = newAction;
        break;
      }
      case 'remove': {
        if (!state.byId[delta.action_id]) {
          return;
        }
        delete state.byId[delta.action_id];
        break;
      }
      case 'update': {
        const existing = state.byId[delta.action_id];
        if (!existing) {
          return;
        }

        // update status to ready so executor can pick action again
        // iff type or description changed
        if (
          (delta?.action.type && existing.type !== delta.action.type) ||
          (delta?.action.description && existing.description !== delta.action.description)
        ) {
          delta.action.status = 'ready';
        }

        Object.assign(existing, delta.action);

        existing.updated_at = moment().unix();
        break;
      }
    }
  }
}

export default slice.reducer;
export const actionsActions = slice.actions;
